/**
 * Built-in cell formatters that can be referenced from `ColumnDef.format`.
 *
 * Formatters are small, pure, and composable — pipe them with {@link composeFormatters} when one
 * column needs more than one transformation (e.g., beautify JSON then strip HTML).
 */

import type { CellFormatter } from './types';
import { htmlToPlainText } from './htmlToPlainText';

/** ISO date (YYYY-MM-DD) — sortable, locale-independent, the canonical CSV date format. */
export const formatIsoDate: CellFormatter = value => {
  if (value === null || value === undefined || value === '') return '';
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    return value.toISOString().slice(0, 10);
  }
  const parsed = new Date(String(value));
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toISOString().slice(0, 10);
};

/** ISO date-time (YYYY-MM-DDTHH:mm:ssZ). */
export const formatIsoDateTime: CellFormatter = value => {
  if (value === null || value === undefined || value === '') return '';
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    return value.toISOString();
  }
  const parsed = new Date(String(value));
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toISOString();
};

/**
 * Human-friendly locale date (e.g., "Apr 23, 2026"). Prefer `formatIsoDate` when the value will be
 * re-sorted in Excel — locale strings do not sort chronologically.
 */
export const formatLocaleDate =
  (locale = 'en-US'): CellFormatter =>
  value => {
    if (value === null || value === undefined || value === '') return '';
    const parsed = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

/**
 * Strip HTML and preserve structural whitespace — the right default for any column that can
 * contain rich-text editor output (headlines, descriptions, diff bodies).
 */
export const formatRichText: CellFormatter = value => {
  if (value === null || value === undefined) return '';
  return htmlToPlainText(String(value));
};

/**
 * Format values that are sometimes JSON (string payload or object), sometimes plain text.
 *
 * - Already-an-object → pretty-printed JSON.
 * - JSON-looking string → parsed and pretty-printed; falls back to the original string on parse
 *   failure (so `{link=..., date=...}` Java-style blobs pass through unchanged).
 * - Everything else → string form.
 *
 * Pretty printing uses 2-space indent and a newline per field so the cell is readable once Excel
 * wraps it. A single-line `stringify` would be impossible to scan.
 */
export const formatJsonBlob: CellFormatter = value => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  const str = String(value).trim();
  if (!str.startsWith('{') && !str.startsWith('[')) return str;
  try {
    const parsed = JSON.parse(str);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return str;
  }
};

/**
 * Compose formatters left-to-right. The first formatter receives the raw value; each subsequent
 * formatter receives the previous formatter's string output in `value`. The row reference is
 * forwarded unchanged so downstream formatters can still reach sibling fields.
 *
 * @example
 * format: composeFormatters(formatJsonBlob, formatRichText)
 */
export function composeFormatters<TRow = unknown>(
  ...fns: Array<CellFormatter<TRow, unknown>>
): CellFormatter<TRow, unknown> {
  return (value, row) => {
    let current: unknown = value;
    for (const fn of fns) {
      const result = fn(current, row);
      current = result ?? '';
    }
    return current as string;
  };
}
